import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { COLOR_MODE_KEY, themeProperties } from '@/styles/theme';
import { useMediaQuery } from 'usehooks-ts';
import { useRouter } from 'next/router';
import { createContextFactory } from '@/contexts/createContextFactory';
import { applyAppColorMode, shouldUseAppDarkMode } from '@/styles/appDarkMode';

type ThemeMode = 'light' | 'dark';

interface ColorModeContextValue {
  colorMode: string | undefined;
  setColorMode: (value: ThemeMode) => void;
}

const ColorModeContext = createContext<ColorModeContextValue | null>(null);
ColorModeContext.displayName = 'ColorMode';

const ColorModeProvider = ({ children }: { children: ReactNode }) => {
  const [colorMode, setRawColorMode] = useState<string | undefined>(undefined);
  const systemPrefers = useMediaQuery('(prefers-color-scheme: dark)');
  // 라이트 고정 화면(세일즈)은 경로로 가른다 — 같은 웹뷰 안에서 경로가 바뀌어도 따라가게
  const { pathname } = useRouter();
  const firstRender = useRef(true);

  const setColorMode = useCallback((value: ThemeMode) => {
    const root = window.document.documentElement;

    Object.entries(themeProperties).forEach(([name, colorByTheme]) => {
      const cssVarName = `--${name}`;
      root.style.setProperty(cssVarName, colorByTheme[value]);
    });

    // setRawColorMode(value); // TODO: dark 모드 끈 상태 -> 추후 키기
    setRawColorMode('light');
    window.localStorage.setItem(COLOR_MODE_KEY, value);
  }, []);

  useEffect(() => {
    // 웹(PC · 모바일 브라우저)의 다크 모드는 아직 끈 상태. 앱 웹뷰만 기기 다크를 따른다 (styles/appDarkMode.ts)
    // 기존 CSS 변수(--body-color 등)는 라이트 값 그대로 두고, 다크 색은 --dk-* 로만 바꾼다
    const appMode = shouldUseAppDarkMode(systemPrefers, pathname)
      ? 'dark'
      : 'light';
    applyAppColorMode(appMode);
    if (firstRender.current) {
      setRawColorMode(appMode);
      firstRender.current = false;
    } else {
      setColorMode('light');
      setRawColorMode(appMode);
    }
  }, [systemPrefers, pathname, setColorMode]);

  return (
    <ColorModeContext.Provider value={{ colorMode, setColorMode }}>
      {children}
    </ColorModeContext.Provider>
  );
};

const useColorModeContext = () => createContextFactory(ColorModeContext);

export { ColorModeProvider, useColorModeContext };
